import { useModalLifecycle } from './useModalLifecycle'
import React, { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  badge?: React.ReactNode
  headerExtra?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  children: React.ReactNode
  footer?: React.ReactNode
  hasUnsavedChanges?: boolean
  unsavedConfirmMessage?: string
  ariaLabel?: string
  zIndex?: number
  closeOnBackdrop?: boolean
}

/**
 * Componente Modal Reutilizable y Estandarizado — Team Spain
 *
 * Cumple con DESIGN_SYSTEM:
 * - Superficie 100% opaca (--surface-card: #ffffff).
 * - Fondo exterior oscurecido (--modal-backdrop-bg: rgba(10, 19, 32, 0.75)) con blur suave.
 * - Bloqueo de scroll en <body> mientras está montado.
 * - Cierre accesible con tecla Escape y comprobación de cambios sin guardar.
 * - Retorno de foco al elemento de origen tras el cierre.
 * - Cabecera fija con objetivo táctil >= 44 px, cuerpo con scroll interno y pie de acciones fijo.
 * - Renderizado en document.body mediante React Portal para evitar solapamientos de z-index.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  badge,
  headerExtra,
  size = 'md',
  children,
  footer,
  hasUnsavedChanges = false,
  unsavedConfirmMessage = 'Tienes cambios sin guardar. ¿Deseas descartarlos y cerrar?',
  ariaLabel,
  zIndex = 1050,
  closeOnBackdrop = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElementRef = useRef<HTMLElement | null>(null)

  // Manejar intento de cierre con protección de cambios sin guardar
  const handleCloseAttempt = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(unsavedConfirmMessage)
      if (!confirmLeave) return
    }
    onClose()
  }, [hasUnsavedChanges, unsavedConfirmMessage, onClose])

  useModalLifecycle(isOpen, handleCloseAttempt)

  // Foco y retorno de foco, independientes del bloqueo compartido
  useEffect(() => {
    if (!isOpen) return

    previousActiveElementRef.current = document.activeElement as HTMLElement | null

    // Foco en el modal para accesibilidad
    const timer = setTimeout(() => {
      modalRef.current?.focus?.()
    }, 50)

    return () => {
      clearTimeout(timer)
      previousActiveElementRef.current?.focus?.()
    }
  }, [isOpen])

  if (!isOpen) return null

  const sizeClass = `modal-${size}`

  const modalNode = (
    <div
      className="modal-backdrop"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : ariaLabel || 'Diálogo modal'}
      onClick={(e) => {
        if (e.target === e.currentTarget && closeOnBackdrop) {
          handleCloseAttempt()
        }
      }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`modal-card ${sizeClass}`}
        onClick={(e) => e.stopPropagation()}
        style={{ outline: 'none' }}
      >
        {/* Cabecera consistente y fija */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0, flex: 1 }}>
            {icon && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--brand-red)',
                  flexShrink: 0,
                }}
              >
                {icon}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <h2
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 700,
                    margin: 0,
                    color: 'var(--text-main)',
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </h2>
                {badge}
              </div>
              {subtitle && (
                <p
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--text-secondary)',
                    margin: '0.15rem 0 0 0',
                    lineHeight: 1.3,
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, marginLeft: '0.75rem' }}>
            {headerExtra}
            <button
              type="button"
              onClick={handleCloseAttempt}
              aria-label="Cerrar modal"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 'var(--touch-target-min)',
                minHeight: 'var(--touch-target-min)',
                padding: '0.45rem',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-secondary)',
                backgroundColor: 'transparent',
                border: '1px solid transparent',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--surface-subtle)'
                e.currentTarget.style.color = 'var(--text-main)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.color = 'var(--text-secondary)'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Cuerpo con scroll interno */}
        <div className="modal-body">{children}</div>

        {/* Pie fijo con acciones uniformes */}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )

  // Si estamos en entorno navegador con document.body, usar createPortal
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalNode, document.body)
  }

  return modalNode
}
